"use client";

import { useDayInLife } from "@/hooks/life-hooks";
import { AnimatePresence, motion } from "framer-motion";
import {
  Loader2,
  Moon,
  Pause,
  Play,
  Sparkles,
  Sun,
  Sunrise,
  Sunset,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

const SEGMENTS = [
  { key: "morning", icon: Sunrise, ar: "الصباح", en: "Morning", overlay: "from-amber-500/40 via-orange-400/20 to-transparent", filter: "brightness(1.05) saturate(1.1)" },
  { key: "noon", icon: Sun, ar: "الظهيرة", en: "Noon", overlay: "from-sky-400/30 via-blue-300/10 to-transparent", filter: "brightness(1.12) saturate(1.05)" },
  { key: "evening", icon: Sunset, ar: "المساء", en: "Evening", overlay: "from-rose-500/45 via-purple-500/25 to-transparent", filter: "brightness(0.95) saturate(1.15) sepia(0.15)" },
  { key: "night", icon: Moon, ar: "الليل", en: "Night", overlay: "from-indigo-900/65 via-slate-900/45 to-indigo-800/30", filter: "brightness(0.7) saturate(0.9) hue-rotate(-10deg)" },
] as const;

const SCENE_MS = 9000;

export default function DayInLife({
  slug,
  title,
  images = [],
}: {
  slug?: string;
  title?: string;
  images?: string[];
}) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const day = useDayInLife();

  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const data = day.data?.day;
  const seg = SEGMENTS[idx];
  const text = data ? (data as any)[seg.key] : "";
  const img = images.length ? images[idx % images.length] : null;

  const start = () => {
    if (!slug) return;
    day.mutate(
      { slug, locale },
      {
        onSuccess: () => {
          setIdx(0);
          setPlaying(true);
          setOpen(true);
        },
      },
    );
  };

  /* voiceover (free, browser SpeechSynthesis) */
  const speak = useCallback(
    (t: string) => {
      if (muted || !t || typeof window === "undefined" || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(t);
      u.lang = isAr ? "ar-SA" : "en-US";
      u.rate = 0.98;
      const v = window.speechSynthesis
        .getVoices()
        .find((x) => x.lang.startsWith(isAr ? "ar" : "en"));
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    },
    [muted, isAr],
  );

  /* auto-advance + narration */
  useEffect(() => {
    if (!open || !playing || !data) return;
    speak(text);
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % SEGMENTS.length);
    }, SCENE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playing, idx, data]);

  /* cleanup speech on close */
  useEffect(() => {
    if (!open && typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, [open]);

  if (!slug) return null;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="my-10">
      {/* ── CTA ── */}
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0a2a6e] via-[#0d3589] to-[#071f52] p-7 text-white">
        <div className="pointer-events-none absolute -top-12 -right-8 size-48 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="size-12 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-[#0d3589] shadow-lg">
              <Sparkles size={24} />
            </div>
            <div>
              <h3 className="font-black text-xl">{isAr ? "عِش فيه يوماً كاملاً" : "Live a full day here"}</h3>
              <p className="text-sm text-blue-100/70">
                {isAr ? "جولة سينمائية بصور العقار — من الصباح للّيل" : "A cinematic tour — morning to night"}
              </p>
            </div>
          </div>
          <button
            onClick={start}
            disabled={day.isPending}
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-[#0d3589] font-extrabold px-6 py-3.5 hover:brightness-105 active:scale-95 transition-all disabled:opacity-60"
          >
            {day.isPending ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} className="fill-[#0d3589]" />}
            {day.isPending ? (isAr ? "نجهّز الجولة..." : "Preparing...") : isAr ? "شغّل الجولة" : "Play tour"}
          </button>
        </div>
      </div>

      {day.isError && (
        <p className="text-sm text-red-600 mt-3">{isAr ? "تعذّر توليد الجولة، حاول مجدداً." : "Failed, retry."}</p>
      )}

      {/* ── Cinematic player ── */}
      <AnimatePresence>
        {open && data && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
          >
            {/* scene */}
            <div className="relative w-full h-full overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9 }}
                  className="absolute inset-0"
                >
                  {img ? (
                    <div
                      className="absolute inset-0 animate-[kenburns_10s_ease-out_forwards]"
                      style={{ filter: seg.filter }}
                    >
                      <Image src={img} alt="" fill priority className="object-cover" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950" />
                  )}
                  <div className={`absolute inset-0 bg-gradient-to-t ${seg.overlay}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/40" />
                </motion.div>
              </AnimatePresence>

              {/* segment label */}
              <div className="absolute top-6 inset-x-0 flex flex-col items-center gap-3 px-4">
                <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-2 text-white">
                  <seg.icon size={18} className="text-amber-300" />
                  <span className="font-bold text-sm">{isAr ? seg.ar : seg.en}</span>
                </div>
                {/* progress segments */}
                <div className="flex gap-1.5 w-full max-w-md">
                  {SEGMENTS.map((_, i) => (
                    <div key={i} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
                      <div
                        className={`h-full bg-amber-300 ${i < idx ? "w-full" : i === idx && playing ? "animate-[grow_9s_linear_forwards]" : i === idx ? "w-1/2" : "w-0"}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* caption */}
              <div className="absolute bottom-0 inset-x-0 p-6 sm:p-10">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="max-w-3xl mx-auto text-center text-white text-lg sm:text-2xl font-bold leading-relaxed drop-shadow-lg"
                  >
                    {text}
                  </motion.p>
                </AnimatePresence>
                {title && (
                  <p className="text-center text-white/50 text-sm mt-4">{title}</p>
                )}
              </div>

              {/* controls */}
              <div className="absolute top-5 left-5 flex gap-2">
                <button
                  onClick={() => {
                    if (typeof window !== "undefined") window.speechSynthesis?.cancel();
                    setMuted((m) => {
                      if (m) speak(text); // unmuting → speak current
                      return !m;
                    });
                  }}
                  className="size-10 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 flex items-center justify-center text-white"
                >
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="size-10 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 flex items-center justify-center text-white"
                >
                  {playing ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <button
                  onClick={() => {
                    setOpen(false);
                    setPlaying(false);
                  }}
                  className="size-10 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 flex items-center justify-center text-white"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        @keyframes kenburns {
          0% { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.15) translate(-1.5%, -1.5%); }
        }
        @keyframes grow {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
}
